import {
  MARKETING_SLICE_MAX, NEWSLETTER_SENDING_STALE_MS, isStaleSendingClaim, parseSliceSize,
  type MarketingFirstSlice, type NewsletterReach, type NewsletterSendStatus, type NewsletterSendSummary,
} from '@evi/shared';

/**
 * A newsletter send runs in the background on the Api (23 October 2026):
 * POST /admin/newsletter/:id/send answers 202 once the draft is claimed, and
 * the page polls GET /admin/newsletter/:id/status until the status leaves
 * 'sending'. These are the page's pure pieces.
 */

/** How often the page checks a send that runs in the background. */
export const SEND_POLL_MS = 5_000;

/**
 * react-query refetchInterval: every 5 s until the answer is no longer
 * 'sending', or the send has stopped: a claim older than the Api's takeover
 * window means the process that held it is gone (an Api deploy or restart),
 * so polling would read 'sending' forever. The page offers Resume instead.
 */
export function nextSendPoll(
  latest: Pick<NewsletterSendStatus, 'status'> & { sending_at?: string | null } | undefined,
  now: Date = new Date(),
): number | false {
  if (!latest) return SEND_POLL_MS;
  if (latest.status !== 'sending') return false;
  return sendStopped(latest, now) ? false : SEND_POLL_MS;
}

/** The send claimed the draft and its process is gone: Resume is safe (the
 *  Api takes the claim over, and nobody already reached gets a second copy). */
export function sendStopped(s: Pick<NewsletterSendStatus, 'status'> & { sending_at?: string | null }, now: Date = new Date()): boolean {
  return isStaleSendingClaim(s, now);
}

/**
 * How long until a 'sending' claim turns stale, plus a second, so the page
 * can schedule one re-render for that moment (23 October 2026, review). With
 * the Api restarted mid-send, every poll returns the same row, React Query
 * keeps the same data, and nothing else re-renders the page: without this it
 * said "Checking every 5 seconds" for good after polling had stopped, and
 * Resume never showed. Null when there is nothing to wait for.
 */
export function msUntilSendStops(sendingAt: string | null | undefined, now: Date = new Date()): number | null {
  if (!sendingAt) return null;
  const at = Date.parse(sendingAt);
  if (Number.isNaN(at)) return null;
  return Math.max(0, at + NEWSLETTER_SENDING_STALE_MS - now.getTime() + 1_000);
}

/** The Resume confirm. */
export const RESUME_CONFIRM = 'Resume this send? It stopped part way (the Api restarted). Everyone it already reached is skipped, '
  + 'so nobody gets a second copy; the rest are sent now. This cannot be undone.';

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** "Sent to 2398 recipients (600 excluded for consent, 2 failed)." */
export function sentSummaryText(s: NewsletterSendSummary | null | undefined): string {
  if (!s) return 'Sent.';
  return `Sent to ${plural(s.sent, 'recipient')} (${s.excluded_for_consent} excluded for consent, ${s.failed} failed).`;
}

/**
 * The first slice (23 October 2026, Warren's item 6). The first Send asks a
 * person for "send to at most N recipients": the field starts empty, and
 * Send stays off until it holds a whole number from 1 to 20,000. The rest of
 * the audience stays pending until a person sends the remainder.
 */
export function sliceSizeFromInput(raw: string): number | null {
  return parseSliceSize(raw);
}

/** Why the slice field is not accepted yet, or null when it is. */
export function sliceInputProblem(raw: string): string | null {
  if (raw.trim() === '') return 'Type how many recipients the first slice goes to.';
  return sliceSizeFromInput(raw) === null ? `A whole number from 1 to ${MARKETING_SLICE_MAX}.` : null;
}

/** What the first-slice dialog says will happen for this size. */
export function sliceConfirmText(n: number, reach: Pick<NewsletterReach, 'reachable'> | null): string {
  if (!reach) return `Send the first slice to at most ${plural(n, 'recipient')} now. Everyone else stays pending until someone sends the remainder. This cannot be undone.`;
  const now = Math.min(n, reach.reachable);
  const left = Math.max(0, reach.reachable - n);
  return left > 0
    ? `Send to ${plural(now, 'recipient')} now, of ${reach.reachable} reachable. The other ${left} stay pending until someone sends the remainder. This cannot be undone.`
    : `All ${plural(reach.reachable, 'reachable recipient')} fit in this slice, so this sends the whole newsletter now. This cannot be undone.`;
}

/** "First slice: sent to 48 of at most 50 (2 failed). 1,203 pending." */
export function partialSummaryText(s: MarketingFirstSlice | null | undefined): string {
  if (!s) return 'The first slice went out. The rest is pending.';
  return `First slice: sent to ${plural(s.sent, 'recipient')} (at most ${s.max_recipients}; ${s.failed} failed). `
    + `${s.pending} pending until someone sends the remainder.`;
}

/** The remainder confirm. */
export function remainderConfirmText(s: MarketingFirstSlice | null | undefined, reach: Pick<NewsletterReach, 'reachable'> | null): string {
  const n = reach ? reach.reachable : (s?.pending ?? null);
  const who = n === null ? 'everyone still pending' : `the ${plural(n, 'recipient')} still pending`;
  return `Send the remainder to ${who}? Everyone the first slice reached is skipped. This cannot be undone.`;
}

/** The banner once a background send has finished, or stopped and was handed back. */
export function sendOutcomeBanner(
  s: Pick<NewsletterSendStatus, 'status' | 'send_summary' | 'send_error'> & { first_slice?: MarketingFirstSlice | null },
): { kind: 'ok' | 'err'; text: string } {
  if (s.status === 'sent') return { kind: 'ok', text: sentSummaryText(s.send_summary) };
  if (s.status === 'partially_sent' && s.send_error) {
    return { kind: 'err', text: `The remainder stopped: ${s.send_error}. It can be sent again; everyone already reached is skipped.` };
  }
  if (s.status === 'partially_sent') return { kind: 'ok', text: partialSummaryText(s.first_slice) };
  if (s.status === 'approved') {
    // A send now also stops part way when the provider keeps refusing, or
    // ends with nothing sent: it goes back to approved either way.
    return {
      kind: 'err',
      text: `The send stopped: ${s.send_error ?? 'no reason was recorded'}. It can be sent again; everyone it already reached is skipped.`,
    };
  }
  return { kind: 'ok', text: `Status: ${s.status}.` };
}
